/**
 * CoCM Billing facade.
 *
 * Stable UI-facing surface for the monthly billing-summary write operations
 * (D3/D7): upsert the summary Observation, toggle submission, reopen a closed
 * month. Bridges the UI to the `@benchmark/core` action stack.
 *
 * @agentic connections
 */

import type { MedplumClient } from "@medplum/core";
import type { Composition, Observation } from "@medplum/fhirtypes";
import {
  reopenCoCMBillingSummary,
  submitCoCMBillingPacket,
  reopenCoCMBillingPacket,
  upsertCoCMBillingSummary,
  type ReopenCoCMBillingPacketInput,
  type ReopenCoCMBillingSummaryInput,
  type SubmitCoCMBillingPacketInput,
  type SubmitCoCMBillingPacketResult,
  type UpsertCoCMBillingSummaryResult,
} from "@benchmark/core/actions";
import type { CoCMBillingSummarySnapshot } from "@benchmark/core/types";

/**
 * Create a CoCM billing facade bound to a MedplumClient.
 * @agentic connections
 */
export function createCoCMBillingFacade(medplum: MedplumClient) {
  return {
    /**
     * Create or merge the monthly billing-summary Observation (open months
     * only). The UI never closes a month — that's the CLI's job (D7) — so this
     * facade intentionally does not accept `lockStatus`.
     * @agentic connections
     */
    upsertSummary: (
      snapshot: CoCMBillingSummarySnapshot,
    ): Promise<UpsertCoCMBillingSummaryResult> =>
      upsertCoCMBillingSummary(medplum, { snapshot }),

    /** Reopen a closed month for correction (audited). @agentic connections */
    reopen: (input: ReopenCoCMBillingSummaryInput): Promise<Observation> =>
      reopenCoCMBillingSummary(medplum, input),

    /**
     * Submit + lock the attested billing packet (Composition + ChargeItems +
     * frozen minutes Observation) atomically (D18/D19). @agentic connections
     */
    submitPacket: (
      input: SubmitCoCMBillingPacketInput,
    ): Promise<SubmitCoCMBillingPacketResult> =>
      submitCoCMBillingPacket(medplum, input),

    /** Reopen a locked packet for an audited correction (D7). @agentic connections */
    reopenPacket: (
      input: ReopenCoCMBillingPacketInput,
    ): Promise<Composition | undefined> =>
      reopenCoCMBillingPacket(medplum, input),
  };
}
